import {
  GUIDE_ACTIVITY_TIMES,
  GUIDE_CATEGORIES,
  GUIDE_ROUTE_TYPES,
  isGuideStatus,
} from "./types";
import type {
  GuideActivityTime,
  GuideCity,
  GuideCategory,
  GuideDraft,
  GuideEditorSnapshot,
  GuideFoodAndDrink,
  GuideItinerary,
  GuideItineraryDay,
  GuideItineraryStop,
  GuideListCover,
  GuideListItem,
  GuideListPage,
  GuideNeighbourhood,
  GuidePhoto,
  GuidePlace,
  GuidePlaceReference,
  GuideRouteType,
  GuideSectionsDraft,
  GuideStaleSummary,
  GuideWriteResult,
  GuideWriteOutcome,
} from "./types";

type UnknownRecord = Record<string, unknown>;

export function malformedGuidesResponse(path: string): {
  code: string;
  message: string;
} {
  return {
    code: "client.malformed_response",
    message: `Malformed guides response at ${path}`,
  };
}

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function asRecord(value: unknown, path: string): UnknownRecord {
  if (!isRecord(value)) throw malformedGuidesResponse(path);
  return value;
}

function asArray(value: unknown, path: string): unknown[] {
  if (!Array.isArray(value)) throw malformedGuidesResponse(path);
  return value;
}

function requiredString(
  record: UnknownRecord,
  key: string,
  path: string,
): string {
  const value = record[key];
  if (typeof value !== "string" || value.length === 0) {
    throw malformedGuidesResponse(path === "" ? key : `${path}.${key}`);
  }
  return value;
}

function nonNegativeInteger(
  record: UnknownRecord,
  key: string,
  path: string,
): number {
  const value = record[key];
  if (
    typeof value !== "number" ||
    !Number.isInteger(value) ||
    value < 0
  ) {
    throw malformedGuidesResponse(path === "" ? key : `${path}.${key}`);
  }
  return value;
}

function parseGuideCity(value: unknown, path: string): GuideCity {
  const record = asRecord(value, path);
  return {
    cityId: requiredString(record, "cityId", path),
    name: requiredString(record, "name", path),
  };
}

function parseGuideCover(value: unknown, path: string): GuideListCover {
  const record = asRecord(value, path);
  const rawVariants = record["variants"];
  let variants: GuideListCover["variants"];
  if (rawVariants === null) {
    variants = null;
  } else {
    const variantRecord = asRecord(rawVariants, `${path}.variants`);
    variants = {
      card: requiredString(variantRecord, "card", `${path}.variants`),
      hero: requiredString(variantRecord, "hero", `${path}.variants`),
      thumb: requiredString(variantRecord, "thumb", `${path}.variants`),
    };
  }

  return {
    url: requiredString(record, "url", path),
    variants,
    alt: requiredString(record, "alt", path),
    attributionState: normalizeAttributionState(
      record["attributionState"],
      `${path}.attributionState`,
    ),
  };
}

function parseGuideListItem(value: unknown, path: string): GuideListItem {
  const record = asRecord(value, path);
  const status = requiredString(record, "status", path);
  if (!isGuideStatus(status)) {
    throw malformedGuidesResponse(`${path}.status`);
  }

  const rawCover = record["cover"];
  const updatedAt = requiredString(record, "updatedAt", path);
  if (!Number.isFinite(Date.parse(updatedAt))) {
    throw malformedGuidesResponse(`${path}.updatedAt`);
  }

  return {
    id: requiredString(record, "id", path),
    title: requiredString(record, "title", path),
    city: parseGuideCity(record["city"], `${path}.city`),
    status,
    cover: rawCover === null ? null : parseGuideCover(rawCover, `${path}.cover`),
    itineraryCount: nonNegativeInteger(record, "itineraryCount", path),
    placeCount: nonNegativeInteger(record, "placeCount", path),
    stalePlaces: nonNegativeInteger(record, "stalePlaces", path),
    updatedAt,
  };
}

export function parseGuideListPage(value: unknown): GuideListPage {
  const record = asRecord(value, "data");
  const items = asArray(record["items"], "items");
  const rawCursor = record["nextCursor"];
  if (rawCursor !== null && typeof rawCursor !== "string") {
    throw malformedGuidesResponse("nextCursor");
  }

  return {
    items: items.map((item, index) =>
      parseGuideListItem(item, `items[${index}]`),
    ),
    nextCursor: rawCursor,
  };
}

export function parseGuideStaleSummary(value: unknown): GuideStaleSummary {
  const record = asRecord(value, "data");
  return {
    guides: nonNegativeInteger(record, "guides", ""),
    places: nonNegativeInteger(record, "places", ""),
  };
}

export function parseGuideEditorSnapshot(value: unknown): GuideEditorSnapshot {
  const card = asRecord(value, "guide");
  const city = asRecord(card["city"], "guide.city");
  const sections = asRecord(card["sections"], "guide.sections");
  const status = guideRequiredString(card, "status", "guide");
  if (!isGuideStatus(status)) throw malformedGuidesResponse("guide.status");

  return {
    id: guideRequiredIdentifier(card, "id", "guide"),
    revision: guideRequiredInteger(card, "revision", "guide", 1),
    status,
    title: guideRequiredString(card, "title", "guide"),
    summary: guideRequiredString(card, "summary", "guide"),
    tagline: guideNullableString(card["tagline"], "guide.tagline"),
    cityId: guideRequiredIdentifier(city, "cityId", "guide.city"),
    cityName: guideRequiredString(city, "name", "guide.city"),
    sections: parseGuideSections(sections),
    places: guideRequiredArray(card, "places", "guide.places").map(
      (place, index) => parseGuidePlace(place, `guide.places[${index}]`),
    ),
    itineraries: guideRequiredArray(
      card,
      "itineraries",
      "guide.itineraries",
    ).map((itinerary, index) =>
      parseGuideItinerary(itinerary, `guide.itineraries[${index}]`),
    ),
  };
}

export function parseGuideEditorCard(value: unknown): GuideDraft {
  return parseGuideEditorSnapshot(value);
}

export function parseGuideWriteResult(value: unknown): GuideWriteResult {
  const result = asRecord(value, "write");
  const outcome = guideRequiredString(result, "outcome", "write");
  if (!isGuideWriteOutcome(outcome)) {
    throw malformedGuidesResponse("write.outcome");
  }

  const replayed = result["replayed"];
  if (typeof replayed !== "boolean") {
    throw malformedGuidesResponse("write.replayed");
  }

  return {
    guideId: guideRequiredIdentifier(result, "guideId", "write"),
    revision: guideRequiredInteger(result, "revision", "write", 1),
    outcome,
    replayed,
    commandId: guideRequiredIdentifier(result, "commandId", "write"),
    details: asRecord(result["details"], "write.details"),
  };
}

function parseGuideSections(record: UnknownRecord): GuideSectionsDraft {
  return {
    factChips: guideArrayOrEmpty(
      record["factChips"],
      "guide.sections.factChips",
    ).map((item, index) => {
      const path = `guide.sections.factChips[${index}]`;
      const value = asRecord(item, path);
      return {
        label: guideRequiredString(value, "label", path),
        value: guideRequiredString(value, "value", path),
      };
    }),
    atAGlance: guideNullableString(
      record["atAGlance"],
      "guide.sections.atAGlance",
    ),
    categoryTiles: guideArrayOrEmpty(
      record["categoryTiles"],
      "guide.sections.categoryTiles",
    ).map((item, index) => {
      const path = `guide.sections.categoryTiles[${index}]`;
      const value = asRecord(item, path);
      const category = guideRequiredString(value, "category", path);
      if (!isGuideCategory(category)) {
        throw malformedGuidesResponse(`${path}.category`);
      }
      return {
        category,
        title: guideRequiredString(value, "title", path),
        caption: guideRequiredString(value, "caption", path),
        photo: parseGuidePhotoField(value, "photo", path),
      };
    }),
    neighbourhoods: hasField(record, "neighbourhoods")
      ? guideArrayOrEmpty(
          record["neighbourhoods"],
          "guide.sections.neighbourhoods",
        ).map((item, index) => {
          const path = `guide.sections.neighbourhoods[${index}]`;
          const value = asRecord(item, path);
          return {
            id: guideRequiredIdentifier(value, "id", path),
            name: guideRequiredString(value, "name", path),
            caption: guideRequiredString(value, "caption", path),
            photo: parseGuidePhotoField(value, "photo", path),
          };
        })
      : legacyNeighbourhoods(record),
    shortlist: hasField(record, "shortlist")
      ? guideArrayOrEmpty(record["shortlist"], "guide.sections.shortlist").map(
          (item, index) => {
            const path = `guide.sections.shortlist[${index}]`;
            const value = asRecord(item, path);
            return {
              entryId: guideRequiredIdentifier(value, "entryId", path),
              tag: guideNullableString(value["tag"], `${path}.tag`),
            };
          },
        )
      : guideIdentifierArray(record["mustSee"], "guide.sections.mustSee").map(
          (entryId) => ({ entryId, tag: null }),
        ),
    foodAndDrink: hasField(record, "foodAndDrink")
      ? parseGuideFoodAndDrink(record["foodAndDrink"])
      : legacyFoodAndDrink(record),
    localExperiences: guideArrayOrEmpty(
      record["localExperiences"],
      "guide.sections.localExperiences",
    ).map((item, index) => {
      const path = `guide.sections.localExperiences[${index}]`;
      const value = asRecord(item, path);
      return {
        entryId: guideNullableIdentifier(value, "entryId", path),
        title: guideRequiredString(value, "title", path),
        caption: guideRequiredString(value, "caption", path),
      };
    }),
    featuredRoutes: guideIdentifierArray(
      record["featuredRoutes"],
      "guide.sections.featuredRoutes",
    ),
    knowBeforeYouGo: hasField(record, "knowBeforeYouGo")
      ? guideArrayOrEmpty(
          record["knowBeforeYouGo"],
          "guide.sections.knowBeforeYouGo",
        ).map((item, index) => {
          const path = `guide.sections.knowBeforeYouGo[${index}]`;
          const value = asRecord(item, path);
          return {
            icon: guideRequiredString(value, "icon", path),
            title: guideRequiredString(value, "title", path),
            text: guideRequiredString(value, "text", path),
          };
        })
      : guideStringArray(record["quickTips"], "guide.sections.quickTips").map(
          (text) => ({ icon: "info", title: "", text }),
        ),
  };
}

function parseGuidePlace(value: unknown, path: string): GuidePlace {
  const record = asRecord(value, path);
  const status = guideRequiredString(record, "status", path);
  if (!isPlaceStatus(status)) {
    throw malformedGuidesResponse(`${path}.status`);
  }

  return {
    entryId: guideRequiredIdentifier(record, "entryId", path),
    placeId: guideNullableIdentifier(record, "placeId", path),
    seedName: guideRequiredNullableString(record, "seedName", path),
    name: guideRequiredNullableString(record, "name", path),
    category: guideRequiredNullableString(record, "category", path),
    categoryKey: guideRequiredNullableCategory(record, "categoryKey", path),
    district: guideRequiredNullableString(record, "district", path),
    neighbourhoodId: guideNullableIdentifier(record, "neighbourhoodId", path),
    note: guideRequiredNullableString(record, "note", path),
    status,
    photo: parseAdminGuidePhotoField(record, path),
    usedInItineraries: guideRequiredArray(
      record,
      "usedInItineraries",
      `${path}.usedInItineraries`,
    ).map((usage, index) => {
      const usagePath = `${path}.usedInItineraries[${index}]`;
      const usageRecord = asRecord(usage, usagePath);
      return {
        itineraryId: guideRequiredIdentifier(
          usageRecord,
          "itineraryId",
          usagePath,
        ),
        title: guideRequiredString(usageRecord, "title", usagePath),
      };
    }),
  };
}

function parseGuideItinerary(value: unknown, path: string): GuideItinerary {
  const record = asRecord(value, path);
  const routeType = guideRequiredString(record, "routeType", path);
  if (!isGuideRouteType(routeType)) {
    throw malformedGuidesResponse(`${path}.routeType`);
  }

  return {
    itineraryId: guideRequiredIdentifier(record, "itineraryId", path),
    title: guideRequiredString(record, "title", path),
    summary: guideRequiredNullableString(record, "summary", path),
    routeType,
    order: guideRequiredInteger(record, "order", path, 1),
    days: guideRequiredArray(record, "days", `${path}.days`).map(
      (day, index) => parseGuideItineraryDay(day, `${path}.days[${index}]`),
    ),
    dayCount: guideRequiredInteger(record, "dayCount", path, 0),
    placeCount: guideRequiredInteger(record, "placeCount", path, 0),
    reviewed: parseGuideItineraryReview(record, path),
    updatedAt: guideRequiredTimestamp(record, "updatedAt", path),
  };
}

function parseGuideItineraryDay(value: unknown, path: string): GuideItineraryDay {
  const record = asRecord(value, path);
  return {
    day: guideRequiredInteger(record, "day", path, 1),
    stops: guideRequiredArray(record, "stops", `${path}.stops`).map(
      (stop, index) => parseGuideItineraryStop(stop, `${path}.stops[${index}]`),
    ),
  };
}

function parseGuideItineraryStop(
  value: unknown,
  path: string,
): GuideItineraryStop {
  const record = asRecord(value, path);
  const rawActivityTime = record["activityTime"];
  let activityTime: GuideActivityTime | null;
  if (rawActivityTime === null) {
    activityTime = null;
  } else if (isGuideActivityTime(rawActivityTime)) {
    activityTime = rawActivityTime;
  } else {
    throw malformedGuidesResponse(`${path}.activityTime`);
  }

  return {
    entryId: guideRequiredIdentifier(record, "entryId", path),
    order: guideRequiredInteger(record, "order", path, 1),
    activityTime,
  };
}

function parseGuideItineraryReview(
  record: UnknownRecord,
  path: string,
): GuideItinerary["reviewed"] {
  if (!hasField(record, "reviewed")) {
    throw malformedGuidesResponse(`${path}.reviewed`);
  }
  const value = record["reviewed"];
  if (value === null) return null;

  const review = asRecord(value, `${path}.reviewed`);
  return {
    by: guideRequiredString(review, "by", `${path}.reviewed`),
    at: guideRequiredTimestamp(review, "at", `${path}.reviewed`),
  };
}

function parseAdminGuidePhotoField(
  record: UnknownRecord,
  path: string,
): GuidePhoto | null {
  return parseGuidePhotoField(record, "photo", path);
}

function parseGuidePhotoField(
  record: UnknownRecord,
  key: string,
  path: string,
): GuidePhoto | null {
  const fieldPath = `${path}.${key}`;
  if (!hasField(record, key) || record[key] === undefined) {
    throw malformedGuidesResponse(fieldPath);
  }
  return parseGuidePhoto(record[key], fieldPath);
}

function parseGuideFoodAndDrink(value: unknown): GuideFoodAndDrink | null {
  if (value === undefined || value === null) return null;
  const record = asRecord(value, "guide.sections.foodAndDrink");
  return {
    intro: guideRequiredString(record, "intro", "guide.sections.foodAndDrink"),
    entryIds: guideIdentifierArray(
      record["entryIds"],
      "guide.sections.foodAndDrink.entryIds",
    ),
  };
}

function legacyFoodAndDrink(record: UnknownRecord): GuideFoodAndDrink | null {
  const entryIds = guideIdentifierArray(
    record["whereToEat"],
    "guide.sections.whereToEat",
  );
  return entryIds.length === 0 ? null : { intro: "", entryIds };
}

function legacyNeighbourhoods(record: UnknownRecord): GuideNeighbourhood[] {
  const usedIds = new Set<string>();
  return guideStringArray(record["districts"], "guide.sections.districts").map(
    (name, index) => {
      const slug = name
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "");
      const baseId = (slug || `district-${index + 1}`).slice(0, 40);
      let id = baseId;
      let suffix = 1;
      while (usedIds.has(id)) {
        const suffixText = `-${suffix}`;
        id = `${baseId.slice(0, 40 - suffixText.length)}${suffixText}`;
        suffix += 1;
      }
      usedIds.add(id);
      return { id, name, caption: "", photo: null };
    },
  );
}

function parseGuidePhoto(value: unknown, path: string): GuidePhoto | null {
  if (value === null) return null;
  const record = asRecord(value, path);
  const kind = guideRequiredString(record, "kind", path);
  const rightsStatus = guideRequiredString(record, "rightsStatus", path);
  if (kind !== "uploaded" && kind !== "external") {
    throw malformedGuidesResponse(`${path}.kind`);
  }
  if (!isRightsStatus(rightsStatus)) {
    throw malformedGuidesResponse(`${path}.rightsStatus`);
  }

  if (!hasField(record, "variants") || record["variants"] === undefined) {
    throw malformedGuidesResponse(`${path}.variants`);
  }
  const rawVariants = record["variants"];
  let variants: GuidePhoto["variants"] = null;
  if (rawVariants !== null) {
    const variantsRecord = asRecord(rawVariants, `${path}.variants`);
    const keys = Reflect.ownKeys(variantsRecord);
    const expectedKeys = new Set(["card", "hero", "thumb"]);
    if (
      keys.length !== expectedKeys.size ||
      keys.some((key) => typeof key !== "string" || !expectedKeys.has(key))
    ) {
      throw malformedGuidesResponse(`${path}.variants`);
    }
    variants = {
      card: requiredString(variantsRecord, "card", `${path}.variants`),
      hero: requiredString(variantsRecord, "hero", `${path}.variants`),
      thumb: requiredString(variantsRecord, "thumb", `${path}.variants`),
    };
  }

  const host = guideRequiredNullableString(record, "host", path);
  if (kind === "uploaded" && (variants === null || host !== null)) {
    throw malformedGuidesResponse(
      variants === null ? `${path}.variants` : `${path}.host`,
    );
  }
  if (kind === "external" && variants !== null) {
    throw malformedGuidesResponse(`${path}.variants`);
  }

  const attributionState = normalizeAttributionState(
    record["attributionState"],
    `${path}.attributionState`,
  );
  if ((rightsStatus === "unknown") !== (attributionState === "incomplete")) {
    throw malformedGuidesResponse(`${path}.attributionState`);
  }

  return {
    kind,
    url: requiredString(record, "url", path),
    variants,
    alt: requiredString(record, "alt", path),
    credit: requiredString(record, "credit", path),
    license: requiredString(record, "license", path),
    sourceUrl: requiredString(record, "sourceUrl", path),
    rightsStatus,
    host,
    attributionState,
  };
}

function normalizeAttributionState(
  value: unknown,
  path: string,
): GuidePhoto["attributionState"] {
  if (value === "ok" || value === "complete") return "ok";
  if (value === "incomplete") return "incomplete";
  throw malformedGuidesResponse(path);
}

function hasField(record: UnknownRecord, key: string): boolean {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function guideArrayOrEmpty(value: unknown, path: string): unknown[] {
  if (value === undefined || value === null) return [];
  return asArray(value, path);
}

function guideRequiredArray(
  record: UnknownRecord,
  key: string,
  path: string,
): unknown[] {
  if (!hasField(record, key)) throw malformedGuidesResponse(path);
  return asArray(record[key], path);
}

function guideStringArray(value: unknown, path: string): string[] {
  return guideArrayOrEmpty(value, path).map((item, index) => {
    if (typeof item !== "string") {
      throw malformedGuidesResponse(`${path}[${index}]`);
    }
    return item;
  });
}

function guideIdentifierArray(value: unknown, path: string): string[] {
  return guideArrayOrEmpty(value, path).map((item, index) =>
    guideIdentifier(item, `${path}[${index}]`),
  );
}

function guideRequiredString(
  record: UnknownRecord,
  key: string,
  path: string,
): string {
  const value = record[key];
  if (typeof value !== "string") {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  return value;
}

function guideRequiredNullableString(
  record: UnknownRecord,
  key: string,
  path: string,
): string | null {
  if (!hasField(record, key)) {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  if (record[key] === undefined) {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  return guideNullableString(record[key], `${path}.${key}`);
}

function guideRequiredIdentifier(
  record: UnknownRecord,
  key: string,
  path: string,
): string {
  return guideIdentifier(record[key], `${path}.${key}`);
}

function guideNullableIdentifier(
  record: UnknownRecord,
  key: string,
  path: string,
): string | null {
  const fieldPath = `${path}.${key}`;
  if (!hasField(record, key)) throw malformedGuidesResponse(fieldPath);
  const value = record[key];
  if (value === null) return null;
  return guideIdentifier(value, fieldPath);
}

function guideIdentifier(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw malformedGuidesResponse(path);
  }
  return value;
}

function guideNullableString(value: unknown, path: string): string | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "string") throw malformedGuidesResponse(path);
  return value;
}

function guideRequiredInteger(
  record: UnknownRecord,
  key: string,
  path: string,
  minimum: number,
): number {
  const value = record[key];
  if (
    typeof value !== "number" ||
    !Number.isSafeInteger(value) ||
    value < minimum
  ) {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  return value;
}

function guideRequiredTimestamp(
  record: UnknownRecord,
  key: string,
  path: string,
): string {
  const timestamp = guideRequiredString(record, key, path);
  if (!Number.isFinite(Date.parse(timestamp))) {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  return timestamp;
}

function isGuideCategory(value: string): value is GuideCategory {
  return GUIDE_CATEGORIES.some((category) => category === value);
}

function guideRequiredNullableCategory(
  record: UnknownRecord,
  key: string,
  path: string,
): GuideCategory | null {
  if (!hasField(record, key)) {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  const value = record[key];
  if (value === null) return null;
  if (typeof value !== "string" || !isGuideCategory(value)) {
    throw malformedGuidesResponse(`${path}.${key}`);
  }
  return value;
}

function isGuideRouteType(value: string): value is GuideRouteType {
  return GUIDE_ROUTE_TYPES.some((routeType) => routeType === value);
}

function isGuideActivityTime(value: unknown): value is GuideActivityTime {
  return (
    typeof value === "string" &&
    GUIDE_ACTIVITY_TIMES.some((activityTime) => activityTime === value)
  );
}

function isPlaceStatus(
  value: string,
): value is GuidePlaceReference["status"] {
  return (
    value === "public" ||
    value === "not_public" ||
    value === "unavailable" ||
    value === "unresolved"
  );
}

function isRightsStatus(
  value: string,
): value is GuidePhoto["rightsStatus"] {
  return (
    value === "licensed" ||
    value === "permission" ||
    value === "editorial_free" ||
    value === "unknown"
  );
}

function isGuideWriteOutcome(value: string): value is GuideWriteOutcome {
  return value === "applied" || value === "pending" || value === "recorded";
}
